import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { CmsBannerComponent, CmsService, Page, TranslationService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService, CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription, combineLatest, forkJoin, iif, of } from 'rxjs';
import { concatMap, debounceTime, filter, map, switchMap, take, takeWhile } from 'rxjs/operators';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import {
  GetPreferencesCodesPayload,
  MyaccountB2bunitService,
} from 'src/app/service/myaccount/myaccount-b2bunit.service';
import {
  MyaccountFeaturesConfigService,
  VAT_NUMBER,
} from 'src/app/service/myaccount/myaccount-features-config.service';
import { StorageService } from 'src/app/service/storage/storage.service';

export interface ShimanoResponsiveBannerComponent extends CmsBannerComponent {
  title?: string;
}

@Component({
  selector: 'app-responsive-banner',
  templateUrl: './responsive-banner.component.html',
  styleUrls: ['./responsive-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ResponsiveBannerComponent implements OnInit, OnDestroy, AfterContentChecked {
  responsiveBannerTitle: string;
  requestedForDeletion = false;
  isAdmin = false;
  userSubscription$: Subscription;
  myaccountBannerData$: Observable<any>;
  isDesktop = false;
  isMobile = false;
  notMobile = false;
  cxMobileWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  csDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  taxLabel: string;
  myaccount: any;
  isAccountEditPreferencesPage = false;
  currentBtn: any;
  isRedirect = false;

  getUseDataPayload: GetPreferencesCodesPayload = {
    fields: 'FULL',
  };

  activeCountry$: Observable<string> = this.countryService.getActive();

  showVatNumber$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.myaccountFeaturesConfigSerivce.isMyaccountFeatureEnabled$(activeCountry, VAT_NUMBER)
    )
  );
  currentPageSubscription$: Subscription;

  constructor(
    public component: CmsComponentData<ShimanoResponsiveBannerComponent>,
    private breakpointService: BreakpointService,
    private myAccountB2BUnitService: MyaccountB2bunitService,
    protected cms: CmsService,
    private countryService: ShimanoCountryService,
    private cdr: ChangeDetectorRef,
    private translation: TranslationService,
    private myaccountFeaturesConfigSerivce: MyaccountFeaturesConfigService,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.isDesktop = window.innerWidth >= this.csDesktopWidth;
    this.isMobile = window.innerWidth < this.cxMobileWidth;
    this.notMobile = window.innerWidth > this.cxMobileWidth;

    this.component.data$.subscribe((data) => {
      if (typeof data !== 'undefined' && data !== null) {
        if (data.title && typeof data.title !== 'undefined') {
          this.responsiveBannerTitle = data?.title;
        }
      }
    });

    this.userSubscription$ = this.isMyAccountPageTemplate$
      .pipe(
        concatMap((isMyAccountPageTemplate) => iif(() => isMyAccountPageTemplate, this.taxLabel$, of(null))),
        switchMap((taxLabel: any) => {
          return iif(
            () => taxLabel,
            combineLatest([
              this.translation.translate('myAccount.customerNumber'),
              this.translation.translate('myAccount.customerRegistrationNumber.' + taxLabel),
              this.translation.translate('myAccount.dealershipType.' + taxLabel),
            ]).pipe(debounceTime(0)),
            of(null)
          );
        }),
        takeWhile((data: any) => !!data),
        switchMap(([customerNumberLabel, customerRegistrationNumberLabel, dealershipTypeLabel]) => {
          return iif(
            () => customerNumberLabel && customerRegistrationNumberLabel && dealershipTypeLabel,
            forkJoin([
              of(customerNumberLabel),
              of(customerRegistrationNumberLabel),
              of(dealershipTypeLabel),
              this.myAccountB2BUnitService.getB2BManagement(),
            ]),
            of(null)
          );
        })
      )
      .subscribe(([customerNumberLabel, customerRegistrationNumberLabel, dealershipTypeLabel, b2bManagementData]) => {
        this.myaccount = {
          customerNumberLabel,
          customerRegistrationNumberLabel,
          dealershipTypeLabel,
          ...b2bManagementData,
        };
        this.cdr.detectChanges();
      });

    this.currentPageSubscription$ = this.cms.getCurrentPage().subscribe((currentPageDetails) => {
      if (currentPageDetails && currentPageDetails.label === '/edit-preferences') {
        this.isAccountEditPreferencesPage = true;
      } else {
        this.isAccountEditPreferencesPage = false;
      }
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });

    this.cms.getCurrentPage().subscribe((data) => {
      if (data?.label === '/global') {
        this.isRedirect = true;
      } else {
        this.isRedirect = false;
      }
    });
  }

  ngAfterContentChecked(): void {
    setTimeout(() => {
      this.linesOfBannerName();
    });
  }

  get isMyAccountPageTemplate$(): Observable<boolean> {
    return this.cms.getCurrentPage().pipe(
      filter(Boolean),
      map(
        (currentPage: Page) =>
          currentPage.template === 'MyAccountDiscountsPageTemplate' || currentPage.template === 'MyAccountPageTemplate'
      )
    );
  }

  requestAccount(pageRequested: any) {
    const page = this.storageService.getLocalStorageItem('pageChange');

    if (
      (pageRequested === 'loginLeftImage' && page === 'false') ||
      (pageRequested !== 'loginLeftImage' && page === 'false' && pageRequested !== 'requestAccountLeftImage')
    ) {
      return 'loginLeftImage';
    } else if (pageRequested === 'requestAccountLeftImage' && page === 'true') {
      return 'requestAccountLeftImage';
    } else {
      return true;
    }
  }

  private getB2BManagement(): Observable<any> {
    return this.myAccountB2BUnitService.getB2BManagement();
  }

  get taxLabel$(): Observable<string> {
    return this.countryService.getActive().pipe(
      take(1),
      map((activeCountry) => (activeCountry === 'sg' ? activeCountry : 'default'))
    );
  }

  get isResetPasswordPage$(): Observable<boolean> {
    return this.cms.getCurrentPage().pipe(
      filter(Boolean),
      map((currentPage: Page) => currentPage.label === '/login/pw/change')
    );
  }

  linesOfBannerName(): any {
    // Edit Pref and Discount Buttons
    const editPreferenceBtn = document.querySelector('.myaccount-edit-preferences') as HTMLElement;
    const discountsBtn = document.querySelector('.myaccount-discounts') as HTMLElement;
    // banner container
    const bannerName = document.querySelector('.myaccount-banner-name');

    this.editPreferenceNamePos('');

    this.currentBtn = this.hasEditPreferenceDiscountBtns(editPreferenceBtn, discountsBtn);
    let currentClassName = '';

    if (bannerName !== null) {
      if (this.isDesktop) {
        this.editPreferenceNamePos('272px');
        currentClassName = '';
        return;
      }

      if (this.currentBtn === null) {
        if (this.notMobile) {
          this.editPreferenceNamePos('256px');
          if (bannerName.clientHeight === 120) {
            this.editPreferenceNamePos('290px');
            currentClassName = 'have-three-lines';
          } else if (bannerName.clientHeight === 160) {
            this.editPreferenceNamePos('332px');
            currentClassName = 'have-four-lines';
          } else if (bannerName.clientHeight === 200) {
            this.editPreferenceNamePos('370px');
            currentClassName = 'have-five=lines';
          } else if (bannerName.clientHeight === 240) {
            this.editPreferenceNamePos('415px');
            currentClassName = 'have-six-lines';
          } else {
            currentClassName = '';
          }
        } else if (this.isMobile) {
          this.editPreferenceNamePos('289px');
          if (bannerName.clientHeight === 140) {
            this.editPreferenceNamePos('315px');
            currentClassName = 'mobile-five-lines';
          } else if (bannerName.clientHeight === 168) {
            this.editPreferenceNamePos('345px');
            currentClassName = 'mobile-six-lines';
          } else {
            currentClassName = '';
          }
        }
        return;
      } else {
        if (this.notMobile) {
          this.currentBtn.style.top = '180px';
          this.editPreferenceNamePos('256px');
          if (bannerName.clientHeight === 120) {
            this.editPreferenceNamePos('290px');
            currentClassName = 'have-three-lines';
          } else if (bannerName.clientHeight === 160) {
            this.currentBtn.style.top = '260px';
            this.editPreferenceNamePos('332px');
            currentClassName = 'have-four-lines';
          } else if (bannerName.clientHeight === 200) {
            this.currentBtn.style.top = '300px';
            this.editPreferenceNamePos('370px');
            currentClassName = 'have-five-lines';
          } else if (bannerName.clientHeight === 240) {
            this.currentBtn.style.top = '360px';
            this.editPreferenceNamePos('415px');
            currentClassName = 'have-six-lines';
          } else {
            currentClassName = '';
          }
        } else if (this.isMobile) {
          this.currentBtn.style.top = '224px';
          this.editPreferenceNamePos('289px');
          if (bannerName.clientHeight === 140) {
            this.currentBtn.style.top = '245px';
            this.editPreferenceNamePos('315px');
            currentClassName = 'mobile-five-lines';
          } else if (bannerName.clientHeight === 168) {
            this.currentBtn.style.top = '275px';
            this.editPreferenceNamePos('345px');
            currentClassName = 'mobile-six-lines';
          } else {
            currentClassName = '';
          }
        }
        return;
      }
    }

    return this.toggleClassName(currentClassName);
  }

  hasEditPreferenceDiscountBtns(editPreference: any, discount: any) {
    if (editPreference || discount) {
      return editPreference ? editPreference : discount;
    } else {
      return null;
    }
  }

  editPreferenceNamePos(pixel: string) {
    const editPreferenceHeaderName = document.querySelector('.heading-wrapper') as HTMLElement;
    const bannerName = document.getElementById('banner');
    if (this.isAccountEditPreferencesPage) {
      if (bannerName !== null) {
        if (editPreferenceHeaderName !== null) {
          editPreferenceHeaderName.style.top = pixel;
        }
      }
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.isDesktop = window.innerWidth >= this.csDesktopWidth;
    this.isMobile = window.innerWidth < this.cxMobileWidth;
    this.notMobile = window.innerWidth > this.cxMobileWidth;
  }

  toggleClassName(className: string) {
    const element = document.querySelector('.responsive-banner-container') as HTMLElement;

    if (element !== null) {
      let checkClassName = element?.classList?.contains(className);

      if (element && className !== '') {
        checkClassName && element.classList.length > 4
          ? element.classList.remove(element.classList[3])
          : element.classList.add(className);
      } else if (className === '') {
        element.classList.remove(element.classList[3]);
      }
    }
  }

  ngOnDestroy() {
    if (this.userSubscription$) {
      this.userSubscription$.unsubscribe();
    }
  }
}
